/**
 * PersonaModeSelector Component
 *
 * Radio group for selecting persona generation mode with
 * descriptions for each mode option.
 */

import { GenerationMode } from '@/hooks/usePersonas'

interface ModeOption {
  value: GenerationMode
  label: string
  description: string
  icon: React.ReactNode
}

const MODE_OPTIONS: ModeOption[] = [
  {
    value: 'full',
    label: 'Full',
    description: 'All dimensions included - personality, emotional patterns, behavioral context',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  {
    value: 'partial',
    label: 'Partial',
    description: 'Functional dimensions only - communication style and behavioral patterns (for regulated contexts)',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
        />
      </svg>
    ),
  },
  {
    value: 'minimal',
    label: 'Minimal',
    description: 'Communication style only - maximum privacy protection (for minors or sensitive contexts)',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064"
        />
      </svg>
    ),
  },
]

interface PersonaModeSelectorProps {
  value: GenerationMode
  onChange: (mode: GenerationMode) => void
  disabled?: boolean
  className?: string
}

export default function PersonaModeSelector({
  value,
  onChange,
  disabled = false,
  className = '',
}: PersonaModeSelectorProps) {
  return (
    <div className={`space-y-3 ${className}`}>
      {MODE_OPTIONS.map((option) => (
        <label
          key={option.value}
          className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
            value === option.value
              ? 'border-personic-500 bg-personic-50'
              : 'border-gray-200 hover:border-gray-300 bg-white'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <input
            type="radio"
            name="generation-mode"
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
            disabled={disabled}
            className="mt-1 h-4 w-4 text-personic-600 focus:ring-personic-500 border-gray-300"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span
                className={`${
                  value === option.value ? 'text-personic-600' : 'text-gray-400'
                }`}
              >
                {option.icon}
              </span>
              <span className="font-medium text-gray-900">{option.label}</span>
            </div>
            <p className="mt-1 text-sm text-gray-500">{option.description}</p>
          </div>
        </label>
      ))}
    </div>
  )
}

/**
 * Compact mode badge for display purposes
 */
export function ModeBadge({ mode }: { mode: string }) {
  const colors: Record<string, string> = {
    full: 'bg-emerald-100 text-emerald-700',
    partial: 'bg-amber-100 text-amber-700',
    minimal: 'bg-blue-100 text-blue-700',
    clinical: 'bg-purple-100 text-purple-700',
  }

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
        colors[mode] || 'bg-gray-100 text-gray-700'
      }`}
    >
      {mode}
    </span>
  )
}
